import { useEffect, useRef, useState } from 'react';
import './AnimatedBackground.css';

export const AnimatedBackground = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Verificar se deve desabilitar animação
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const isMobile = window.innerWidth < 768; // Dispositivos móveis
    
    if (prefersReducedMotion || isMobile) {
      // Apenas desenhar fundo estático sem animação
      const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
      const isDark = document.documentElement.classList.contains('theme-dark');
      
      if (isDark) {
        gradient.addColorStop(0, '#0B1220');
        gradient.addColorStop(0.5, '#1a1f3a');
        gradient.addColorStop(1, '#0B1220');
      } else {
        gradient.addColorStop(0, '#FFFFFF');
        gradient.addColorStop(0.5, '#F8FAFC');
        gradient.addColorStop(1, '#FFFFFF');
      }
      
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      return;
    }

    // Configurar tamanho do canvas
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Verificar visibilidade da página (Page Visibility API)
    const handleVisibilityChange = () => {
      setIsVisible(!document.hidden);
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Partículas
    interface Particle {
      x: number;
      y: number;
      radius: number;
      vx: number;
      vy: number;
      opacity: number;
    }

    const particles: Particle[] = [];
    // Reduzir número de partículas de 50 para 25 para melhor performance
    const particleCount = 25;

    // Criar partículas
    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        radius: Math.random() * 2 + 1,
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5,
        opacity: Math.random() * 0.5 + 0.2,
      });
    }

    // Círculos grandes animados
    const circles: Array<{
      x: number;
      y: number;
      radius: number;
      vx: number;
      vy: number;
      opacity: number;
    }> = [];

    for (let i = 0; i < 3; i++) {
      circles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        radius: Math.random() * 200 + 100,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.3,
        opacity: Math.random() * 0.1 + 0.05,
      });
    }

    let animationFrameId: number;
    let isPaused = false;

    const animate = () => {
      // Pausar animação se página não estiver visível
      if (document.hidden || !isVisible) {
        isPaused = true;
        animationFrameId = requestAnimationFrame(animate);
        return;
      }
      
      // Se estava pausado, retomar
      if (isPaused) {
        isPaused = false;
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Desenhar gradiente de fundo
      const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
      const isDark = document.documentElement.classList.contains('theme-dark');
      
      if (isDark) {
        gradient.addColorStop(0, '#0B1220');
        gradient.addColorStop(0.5, '#1a1f3a');
        gradient.addColorStop(1, '#0B1220');
      } else {
        gradient.addColorStop(0, '#FFFFFF');
        gradient.addColorStop(0.5, '#F8FAFC');
        gradient.addColorStop(1, '#FFFFFF');
      }
      
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Atualizar e desenhar círculos grandes
      circles.forEach((circle) => {
        circle.x += circle.vx;
        circle.y += circle.vy;

        // Rebater nas bordas
        if (circle.x < -circle.radius || circle.x > canvas.width + circle.radius) {
          circle.vx *= -1;
        }
        if (circle.y < -circle.radius || circle.y > canvas.height + circle.radius) {
          circle.vy *= -1;
        }

        // Desenhar círculo
        const circleGradient = ctx.createRadialGradient(
          circle.x,
          circle.y,
          0,
          circle.x,
          circle.y,
          circle.radius
        );
        
        if (isDark) {
          circleGradient.addColorStop(0, `rgba(124, 77, 255, ${circle.opacity})`);
          circleGradient.addColorStop(1, 'rgba(124, 77, 255, 0)');
        } else {
          circleGradient.addColorStop(0, `rgba(124, 77, 255, ${circle.opacity * 0.3})`);
          circleGradient.addColorStop(1, 'rgba(124, 77, 255, 0)');
        }

        ctx.fillStyle = circleGradient;
        ctx.beginPath();
        ctx.arc(circle.x, circle.y, circle.radius, 0, Math.PI * 2);
        ctx.fill();
      });

      // Atualizar e desenhar partículas
      particles.forEach((particle) => {
        particle.x += particle.vx;
        particle.y += particle.vy;

        // Rebater nas bordas
        if (particle.x < 0 || particle.x > canvas.width) {
          particle.vx *= -1;
        }
        if (particle.y < 0 || particle.y > canvas.height) {
          particle.vy *= -1;
        }

        // Desenhar partícula
        ctx.fillStyle = isDark
          ? `rgba(124, 77, 255, ${particle.opacity})`
          : `rgba(124, 77, 255, ${particle.opacity * 0.5})`;
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2);
        ctx.fill();
      });

      // Desenhar conexões entre partículas próximas
      particles.forEach((particle, i) => {
        particles.slice(i + 1).forEach((otherParticle) => {
          const dx = particle.x - otherParticle.x;
          const dy = particle.y - otherParticle.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < 150) {
            const opacity = (1 - distance / 150) * 0.2;
            ctx.strokeStyle = isDark
              ? `rgba(124, 77, 255, ${opacity})`
              : `rgba(124, 77, 255, ${opacity * 0.3})`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(particle.x, particle.y);
            ctx.lineTo(otherParticle.x, otherParticle.y);
            ctx.stroke();
          }
        });
      });

      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      cancelAnimationFrame(animationFrameId);
    };
  }, [isVisible]);

  return (
    <canvas
      ref={canvasRef}
      className="animated-background"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 0,
        pointerEvents: 'none',
      }}
    />
  );
};







